import { useRender } from "@base-ui/react/use-render";
import { ChevronRightIcon } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * Plain semantic breadcrumb primitives — no headless library needed, it's
 * just a `<nav>` and an ordered list of links.
 */
export function Breadcrumb(props: ComponentProps<"nav">) {
  return <nav aria-label="Breadcrumb" {...props} />;
}

export function BreadcrumbList({ className, ...props }: ComponentProps<"ol">) {
  return (
    <ol
      className={cn(
        "flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground break-words sm:gap-2.5",
        className,
      )}
      {...props}
    />
  );
}

export function BreadcrumbItem({ className, ...props }: ComponentProps<"li">) {
  return <li className={cn("inline-flex items-center gap-1.5", className)} {...props} />;
}

/** Polymorphic via `render` (same convention as `Button`/`Badge`) so it can render a router `Link` for client-side navigation instead of a full page reload. */
export function BreadcrumbLink({
  className,
  render = <a />,
  ref,
  ...props
}: useRender.ComponentProps<"a">) {
  return useRender({
    render,
    ref,
    props: {
      ...props,
      className: cn("transition-colors hover:text-foreground", className),
    },
  });
}

export function BreadcrumbPage({ className, ...props }: ComponentProps<"span">) {
  return (
    <span
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn("font-medium text-foreground", className)}
      {...props}
    />
  );
}

export function BreadcrumbSeparator({ className, ...props }: ComponentProps<"li">) {
  return (
    <li role="presentation" aria-hidden="true" className={cn("[&>svg]:size-3.5", className)} {...props}>
      <ChevronRightIcon />
    </li>
  );
}
